import { Box, Stack, Typography, useTheme } from '@mui/material';
import { MessageSquare, Phone } from 'lucide-react';
import { type ReactNode, useEffect, useRef } from 'react';
import { isNullish } from 'remeda';
import { ModalContainer } from 'src/components/ModalContainer';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  useSetWorkflowAssignment,
  useWorkflowAssignments,
  type WorkflowAssignmentItem,
} from 'src/graphql/entities/workflow/hooks/use-workflow-assignments';
import {
  useWorkflowsByTrigger,
  type WorkflowByTriggerOption,
} from 'src/graphql/entities/workflow/hooks/use-workflows-by-trigger';
import {
  ORDERED_WORKFLOW_TRIGGER_TYPES,
  WORKFLOW_TRIGGER_LABELS,
  WorkflowTriggerType,
} from 'src/graphql/entities/workflow/workflow.trigger';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';

/**
 * The trigger is what is left of the row after the icon and the label, too narrow to read a
 * workflow name in. This is `SelectMenu`'s own default width, which clears the trigger while
 * still landing inside the dialog; capped so it cannot outgrow a small screen.
 */
const MENU_WIDTH = 'min(17rem, calc(100vw - 2rem))';

const PLATFORM_GROUP = 'Platform workflows';
const PARTNER_GROUP = 'Your workflows';

const TRIGGER_ICONS: Record<WorkflowTriggerType, ReactNode> = {
  [WorkflowTriggerType.InboundPhoneCall]: <Phone size={16} />,
  [WorkflowTriggerType.InboundSms]: <MessageSquare size={16} />,
};

interface AssignmentRowProps {
  triggerType: WorkflowTriggerType;
  assignment?: { workflowId: string };
  platformWorkflows: WorkflowByTriggerOption[];
  partnerWorkflows: WorkflowByTriggerOption[];
  /** Partner 0 only ever sees platform workflows, so the groups carry no information. */
  isPlatformPartner: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}

const AssignmentRow = ({
  triggerType,
  assignment,
  platformWorkflows,
  partnerWorkflows,
  isPlatformPartner,
  disabled,
  onChange,
}: AssignmentRowProps) => {
  const theme = useTheme();
  const label = WORKFLOW_TRIGGER_LABELS[triggerType];
  const options = buildOptions({
    platformWorkflows,
    partnerWorkflows,
    grouped: !isPlatformPartner,
    assignedWorkflowId: assignment?.workflowId,
  });

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      data-testid={`workflow-assignments-row-${triggerType}`}
      sx={{
        py: 1.5,
        borderTop: `1px solid ${theme.palette.divider}`,
        '&:first-of-type': { borderTop: 'none' },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          flex: 'none',
          width: theme.spacing(3.75),
          height: theme.spacing(3.75),
          borderRadius: 1,
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.action.hover,
        }}
      >
        {TRIGGER_ICONS[triggerType]}
      </Box>
      <Stack sx={{ width: theme.spacing(18), flex: 'none', minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          one assignment
        </Typography>
      </Stack>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <SelectMenu
          label={`${label} workflow`}
          placeholder="Select a workflow"
          options={options}
          menuWidth={MENU_WIDTH}
          value={assignment?.workflowId ?? null}
          onChange={onChange}
          disabled={disabled || !options.length}
          sectioned
          search
          searchPlaceholder="Search workflows…"
          emptyText="No workflow matches"
        />
      </Box>
    </Stack>
  );
};

/**
 * Forgets an assignment whose workflow can no longer run, so the stored state and the running
 * state agree instead of the modal showing a selection that silently falls through.
 *
 * Evidence comes from the *owner's* list, never the combined one: if the platform query failed
 * while the partner's succeeded, a combined check would see no match and wipe a good row.
 */
function useClearUnrunnableAssignments({
  partnerId,
  assignmentsByTrigger,
  platformWorkflows,
  partnerWorkflows,
  ready,
  clearAssignment,
  refetchAssignments,
}: {
  partnerId: number;
  assignmentsByTrigger: Partial<Record<WorkflowTriggerType, WorkflowAssignmentItem>>;
  platformWorkflows: Record<WorkflowTriggerType, WorkflowByTriggerOption[]>;
  partnerWorkflows: Record<WorkflowTriggerType, WorkflowByTriggerOption[]>;
  ready: boolean;
  clearAssignment: (input: {
    partnerId: number;
    triggerType: WorkflowTriggerType;
  }) => Promise<void>;
  refetchAssignments: () => void;
}): void {
  // Cleared ids, so a failed write is not retried on every render.
  const attempted = useRef(new Set<string>());

  useEffect(() => {
    if (!ready) return;

    ORDERED_WORKFLOW_TRIGGER_TYPES.forEach((triggerType) => {
      const assignment = assignmentsByTrigger[triggerType];

      if (!assignment) return;

      const owned =
        assignment.workflowPartnerId === PLATFORM_PARTNER_ID
          ? platformWorkflows[triggerType]
          : partnerWorkflows[triggerType];

      // An empty list is ambiguous — none exist, or the query failed — so it proves nothing.
      if (!owned?.length) return;

      const runnable = owned.some(
        (workflow) =>
          workflow.workflowId === assignment.workflowId && workflow.hasActiveVersion,
      );
      const key = `${triggerType}:${assignment.workflowId}`;

      if (runnable || attempted.current.has(key)) return;

      attempted.current.add(key);
      void clearAssignment({ partnerId, triggerType }).then(refetchAssignments);
    });
  }, [
    partnerId,
    assignmentsByTrigger,
    platformWorkflows,
    partnerWorkflows,
    ready,
    clearAssignment,
    refetchAssignments,
  ]);
}

const AssignmentRows = ({ partnerId }: { partnerId: number }) => {
  const selectedPartnerId = partnerId;
  const { assignmentsByTrigger, loading, refetchAssignments } =
    useWorkflowAssignments(selectedPartnerId);
  // Both sides list versionless workflows, disabled with the reason — omitting them reads as
  // "it does not exist" when the fix is one activation away.
  const { workflowsByTrigger: platformWorkflows, loading: platformLoading } =
    useWorkflowsByTrigger(PLATFORM_PARTNER_ID, { includeWithoutActiveVersion: true });
  const { workflowsByTrigger: partnerWorkflows, loading: partnerLoading } =
    useWorkflowsByTrigger(selectedPartnerId, { includeWithoutActiveVersion: true });
  const { setAssignment, clearAssignment, saving } = useSetWorkflowAssignment();

  useClearUnrunnableAssignments({
    partnerId,
    assignmentsByTrigger,
    platformWorkflows,
    partnerWorkflows,
    ready: !loading && !platformLoading && !partnerLoading && !saving,
    clearAssignment,
    refetchAssignments,
  });

  const handleChange = async (triggerType: WorkflowTriggerType, value: string) => {
    const workflowName = [
      ...platformWorkflows[triggerType],
      ...partnerWorkflows[triggerType],
    ].find((workflow) => workflow.workflowId === value)?.name;

    try {
      await setAssignment(
        {
          partnerId: selectedPartnerId,
          triggerType,
          workflowId: value,
        },
        {
          workflowName: workflowName ?? value,
          previousWorkflowId: assignmentsByTrigger[triggerType]?.workflowId ?? null,
        },
      );
      refetchAssignments();
    } catch {
      // The hook surfaced the reason; the select keeps showing what is stored and running.
    }
  };

  return (
    <Stack>
      {ORDERED_WORKFLOW_TRIGGER_TYPES.map((triggerType) => (
        <AssignmentRow
          key={triggerType}
          triggerType={triggerType}
          assignment={assignmentsByTrigger[triggerType]}
          platformWorkflows={platformWorkflows[triggerType]}
          partnerWorkflows={
            selectedPartnerId === PLATFORM_PARTNER_ID ? [] : partnerWorkflows[triggerType]
          }
          isPlatformPartner={selectedPartnerId === PLATFORM_PARTNER_ID}
          disabled={saving || loading || platformLoading || partnerLoading}
          onChange={(value) => void handleChange(triggerType, value)}
        />
      ))}
    </Stack>
  );
};

/** Assignments are per partner, so all-partners mode has nothing to show or write to. */
const AssignmentsBody = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  if (isNullish(selectedPartnerId)) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Select a partner to manage workflow assignments.
      </Typography>
    );
  }

  return <AssignmentRows partnerId={selectedPartnerId} />;
};

/**
 * Per-trigger workflow assignments for the selected partner: one row per trigger type, each a
 * select grouped into platform and partner workflows plus a "Platform default" that clears it.
 *
 * Versions never appear — an assignment points at a workflow and whichever version is active
 * runs, which is what makes a platform rollout one activation instead of one edit per partner.
 */
export const WorkflowAssignmentsModal = () => {
  const { isOpened } = useModalState(Modals.WorkflowAssignments);
  const { closeModal } = useModalMethods(Modals.WorkflowAssignments);

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Workflow assignments"
      onClose={closeModal}
      data-testid="workflow-assignments-modal"
    >
      <Typography variant="body2" color="text.secondary" mb={1}>
        One workflow runs per trigger. Pick a platform workflow or one of yours; the
        workflow&rsquo;s active version is what runs.
      </Typography>
      {isOpened ? <AssignmentsBody /> : null}
    </ModalContainer>
  );
};

function buildOptions({
  platformWorkflows,
  partnerWorkflows,
  grouped,
  assignedWorkflowId,
}: {
  platformWorkflows: WorkflowByTriggerOption[];
  partnerWorkflows: WorkflowByTriggerOption[];
  /** Off for the platform partner: one heading over an all-platform list says nothing. */
  grouped: boolean;
  assignedWorkflowId?: string;
}): SelectMenuOption[] {
  const toOption =
    (group: string, canActivate: boolean) => (workflow: WorkflowByTriggerOption) => ({
      value: workflow.workflowId,
      title: workflow.name,
      // An empty run contributes no rows, so its heading disappears on its own.
      ...(grouped ? { group } : {}),
      // Listed but unpickable — the backend refuses it, so saving would fail unexplained.
      ...(workflow.hasActiveVersion
        ? {}
        : {
            disabled: true,
            description: canActivate
              ? 'No active version — activate one before assigning this workflow.'
              : 'No active version',
          }),
    });

  // The partner's own first: usually what they are reaching for. Ungrouped means partner 0,
  // whose platform workflows are its own.
  const options: SelectMenuOption[] = [
    ...partnerWorkflows.map(toOption(PARTNER_GROUP, true)),
    ...platformWorkflows.map(toOption(PLATFORM_GROUP, !grouped)),
  ];

  // Keeps a deactivated or deleted assignment visible; otherwise the select falls back to its
  // placeholder, which matches neither the stored row nor what runs.
  if (assignedWorkflowId && !options.some((option) => option.value === assignedWorkflowId)) {
    options.push({
      value: assignedWorkflowId,
      title: 'Assigned workflow is no longer active',
      disabled: true,
    });
  }

  return options;
}
